import { useEffect, useState } from "react";
import * as React from "react";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTimeWithTimeZoneLabel,
} from "@src/utils/dateFormatter";
import { useEventParts } from "@src/hooks/useEventParts";

const BG_COLOR = "bg-[#AC5E23]";
const CARD_BG = "bg-[#FCF8EB]";
const CARD_BORDER = "border-[#AC5E23]/20";
const BUTTON_BG = "bg-[#FCF8EB]";
const BUTTON_TEXT = "text-[#AC5E23]";
const TITLE_TEXT = "text-[#FCF8EB]";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  isInvitedToEvent,
}) => {
  // --- Updated Countdown Logic (like Template14) ---
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });
  const [nextEvent, setNextEvent] = useState<any>(null);

  // Function to find the next upcoming event
  const findNextEvent = React.useCallback(() => {
    if (!eventPartsData?.eventParts || eventPartsData.eventParts.length === 0) {
      return null;
    }

    const now = new Date().getTime();

    // Filter events that are in the future and sort by date
    const upcomingEvents = eventPartsData.eventParts
      .filter((event) => new Date(event.time).getTime() > now)
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  }, [eventPartsData]);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const currentNextEvent = findNextEvent();

      if (!currentNextEvent) {
        // No upcoming events
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
        setNextEvent(null);
        return;
      }

      // Update next event if it has changed
      if (!nextEvent || nextEvent.id !== currentNextEvent.id) {
        setNextEvent(currentNextEvent);
      }

      const eventTime = new Date(currentNextEvent.time).getTime();
      const now = new Date().getTime();
      const difference = eventTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({
          days,
          hours,
          minutes,
          seconds,
          isExpired: false,
        });
      } else {
        // Current event has passed, find next one
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [eventPartsData, nextEvent, findNextEvent]);

  const formatNumber = (num: number) => num.toString().padStart(2, "0");

  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  // Add this helper for the subtitle
  const getCountdownSubtitle = () => {
    if (timeLeft.isExpired || !nextEvent) {
      return "All events have concluded";
    }
    return `Next: ${nextEvent.name} on ${formatDateWithOrdinal(
      nextEvent.time,
      nextEvent.eventPartTimezone ?? eventTimezone
    )}`;
  };

  return visibleParts.length > 0 ? (
    <div className={`relative overflow-hidden ${BG_COLOR}`}>
      <div className="absolute inset-0" />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delayChildren: 0.3, staggerChildren: 0.15 },
          },
        }}
        className="relative z-10 py-16 px-4"
      >
        <motion.div
          variants={{
            hidden: { opacity: 0, y: -30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
            },
          }}
          className="text-center mb-16"
        >
          <h3
            className={`font-caudex italic font-semibold lg:font-normal text-3xl lg:text-6xl ${TITLE_TEXT} mb-4`}
          >
            Counting Down To Forever
          </h3>
          {!timeLeft.isExpired && nextEvent && (
            <p className={`${TITLE_TEXT} text-lg lg:text-xl opacity-80 mb-4`}>
              {getCountdownSubtitle()}
            </p>
          )}
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "6rem" }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className={`h-1 ${TITLE_TEXT} mx-auto rounded-full bg-[#FCF8EB]`}
          ></motion.div>
        </motion.div>

        {/* Countdown Section */}
        {!timeLeft.isExpired && (
          <div className="w-full flex flex-col items-center py-4 mt-[-2rem] mb-2 font-caudex">
            <div className="flex gap-4 justify-center items-center flex-wrap w-[80%] md:w-full">
              <div className="bg-[#FCF8EB] rounded-2xl p-4 text-center border border-[#AC5E23]/20 min-w-[120px] min-h-[120px] flex flex-col justify-center items-center">
                <div className="text-5xl font-bold text-[#AC5E23]">
                  {formatNumber(timeLeft.days)}
                </div>
                <div className="text-lg text-[#AC5E23]">Days</div>
              </div>
              <div className="bg-[#FCF8EB] rounded-2xl p-4 text-center border border-[#AC5E23]/20 min-w-[120px] min-h-[120px] flex flex-col justify-center items-center">
                <div className="text-5xl font-bold text-[#AC5E23]">
                  {formatNumber(timeLeft.hours)}
                </div>
                <div className="text-lg text-[#AC5E23]">Hours</div>
              </div>
              <div className="bg-[#FCF8EB] rounded-2xl p-4 text-center border border-[#AC5E23]/20 min-w-[120px] min-h-[120px] flex flex-col justify-center items-center">
                <div className="text-5xl font-bold text-[#AC5E23]">
                  {formatNumber(timeLeft.minutes)}
                </div>
                <div className="text-lg text-[#AC5E23]">Minutes</div>
              </div>
              <div className="bg-[#FCF8EB] rounded-2xl p-4 text-center border border-[#AC5E23]/20 min-w-[120px] min-h-[120px] flex flex-col justify-center items-center">
                <div className="text-5xl font-bold text-[#AC5E23]">
                  {formatNumber(timeLeft.seconds)}
                </div>
                <div className="text-lg text-[#AC5E23]">Seconds</div>
              </div>
            </div>
          </div>
        )}

        {timeLeft.isExpired && (
          <div className="mt-6 text-xl text-[#AC5E23] font-light italic">
            {nextEvent ? "Event in progress!" : "All events have concluded"}
          </div>
        )}

        {/* Event Cards */}
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { delayChildren: 0.3, staggerChildren: 0.15 },
            },
          }}
          className="max-w-5xl mx-auto space-y-6"
        >
          {visibleParts.map((item, idx) => (
            <motion.div
              key={`event-${idx}`}
              variants={{
                hidden: { opacity: 0, y: 50, scale: 0.95 },
                visible: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
                },
              }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <div
                className={`relative p-8 lg:p-10 ${CARD_BG} backdrop-blur-xl rounded-3xl border ${CARD_BORDER} shadow-xl lg:hover:shadow-2xl transition-all duration-500 w-full lg:hover:scale-[1.03]`}
              >
                <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                  {/* Event Icon/Number */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-16 h-16 lg:w-20 lg:h-20 ${BUTTON_BG} rounded-full border border-[#AC5E23] flex items-center justify-center ${BUTTON_TEXT} font-bold text-xl lg:text-2xl shadow-lg`}
                    >
                      {idx + 1}
                    </div>
                  </div>
                  {/* Event Details */}
                  <div className="flex-1 text-center lg:text-left space-y-3">
                    <h4 className="text-2xl lg:text-3xl font-caudex text-[#AC5E23] tracking-wide whitespace-normal break-words">
                      {item.name}
                    </h4>
                    <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 text-[#AC5E23]">
                      {/* Date */}
                      <div className="flex items-center justify-center lg:justify-start gap-2">
                        <span className="font-medium">
                          {/* Add Icon Here!!! */}
                          {formatDateWithOrdinal(
                            item.time,
                            item.eventPartTimezone ?? eventTimezone
                          )}
                        </span>
                      </div>
                      {/* Time */}
                      <div className="flex items-center justify-center lg:justify-start gap-2">
                        <span className="font-medium">
                          {/* Add Icon Here!!! */}
                          {formatEventTimeWithTimeZoneLabel(
                            item.time,
                            item.eventPartTimezone ?? eventTimezone
                          )}
                        </span>
                      </div>
                      {/* Address */}
                      {item.showAddress && (
                        <div className="flex items-center justify-center lg:justify-start gap-2">
                          <span className="font-medium max-w-xs text-[#AC5E23]">
                            {/* ICONNNNNNNNNNN */}
                            {item?.address}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex flex-col gap-3 items-stretch">
                    {item.showAddress && (
                      <div className="inline-flex items-center justify-center w-[140px] bg-[#FCF8EB] text-[#AC5E23] border border-[#AC5E23] rounded-full font-medium hover:bg-[#AC5E23] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer group">
                        <AddToCalendar
                          event={{
                            name: item.name,
                            details: null,
                            location: item?.address,
                            startsAt: item?.time,
                            endsAt: addOneDay(item?.time),
                          }}
                        >
                          <span
                            className={`flex items-center justify-center text-[#AC5E23] font-medium group-hover:text-[#FCF8EB] transition-all duration-300`}
                          >
                            Save Date
                          </span>
                        </AddToCalendar>
                      </div>
                    )}
                    {item.showAddress &&
                      item.hasLiveStream &&
                      liveSchedules && (
                        <DialogModal
                          fillScreen={true}
                          trigger={
                            <button
                              className={`px-6 py-3 w-[140px] bg-[#FCF8EB] text-[#AC5E23] border border-[#AC5E23] rounded-full font-medium hover:bg-[#AC5E23] hover:text-[#FCF8EB] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer`}
                            >
                              Watch Live
                            </button>
                          }
                        >
                          <LiveSchedules schedules={liveSchedules} />
                        </DialogModal>
                      )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom decorative section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center mt-16 px-4"
        >
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FCF8EB]/20 via-[#FCF8EB]/40 to-[#FCF8EB]/20 rounded-2xl blur-xl"></div>
              <div className="relative bg-[#FCF8EB]/90 backdrop-blur-sm rounded-2xl py-8 px-8 border border-[#AC5E23]/30">
                <div className="text-center">
                  <span className="text-[#AC5E23] text-xl lg:text-2xl font-light italic tracking-wide">
                    Love is in the details!
                  </span>
                  <div className="mt-4 flex justify-center">
                    <div className="w-16 h-[2px] bg-[#AC5E23] rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  ) : null;
};

export default Parts;
